import type {
  ViewDefinition,
  ViewInstanceSummary,
} from '@/matic/features/agents/otto/views/types';
import type {
  ObjectRecord,
  ObjectType,
} from '@/matic/features/master-data/types/master-data-types';
import {parseScopeParam} from './scope-utils';

export type ResolvedViewInstance = {
  scopeKey: string;
  definitionId: string;
  definitionName: string;
  generatedAt: string;
  /** Resolved record display name(s) joined with " × "; falls back to the raw
   *  scope key when any record in the scope hasn't loaded into cache yet. */
  scopeLabel: string;
  /** Whether the fallback above happened because a record hasn't resolved
   *  yet. `resolveViewInstanceLabels` has no visibility into the record
   *  query's loading state — callers whose record fetch is still `isLoading`
   *  should treat `scopeLabelUnresolved` rows as pending (e.g. shimmer)
   *  rather than assuming the raw scope key is the final label. */
  scopeLabelUnresolved: boolean;
};

/**
 * Join View instances with their Definition name and a human-readable scope
 * label, resolved client-side from the already-cached MDM records query — the
 * same approach the Otto session-context display labels use (see
 * `featureInventory.md`'s "Otto chat session context" note). Shared by the
 * Library browse hub's Views section and the Home activity digest so both
 * surfaces label a View instance identically.
 */
export function resolveViewInstanceLabels(
  instances: ViewInstanceSummary[],
  definitions: ViewDefinition[],
  types: ObjectType[],
  recordsByType: Record<string, ObjectRecord[]>
): ResolvedViewInstance[] {
  const definitionsById = new Map(definitions.map((d) => [d.id, d]));
  const typesById = new Map(types.map((t) => [t.id, t]));
  return [...instances]
    .map((instance) => {
      const definition = definitionsById.get(instance.definitionId);
      // Aggregate ("Portfolio") scope key: the backend's `aggregate_scope_key`
      // is `type:{typeId}` — a single-segment key distinguished by the literal
      // `type:` prefix (a record scope's first segment is always a UUID). Label
      // it "All {Type}s" rather than falling through to the raw key.
      const aggregateTypeId = instance.scopeKey.startsWith('type:')
        ? instance.scopeKey.slice('type:'.length)
        : undefined;
      if (aggregateTypeId) {
        const typeName = typesById.get(aggregateTypeId)?.name;
        return {
          scopeKey: instance.scopeKey,
          definitionId: instance.definitionId,
          definitionName: definition?.name ?? 'View',
          generatedAt: instance.generatedAt,
          scopeLabel: typeName ? `All ${typeName}s` : 'Portfolio',
          scopeLabelUnresolved: false,
        };
      }

      const scopeItems = parseScopeParam(instance.scopeKey);
      const names = scopeItems.map((item) => {
        const record = (recordsByType[item.typeId] ?? []).find(
          (r) => r.id === item.recordId
        );
        return record?.display ?? record?.code;
      });
      const resolved = names.filter(Boolean);
      const allResolved =
        resolved.length === names.length && resolved.length > 0;
      const scopeLabel = allResolved ? resolved.join(' × ') : instance.scopeKey;
      return {
        scopeKey: instance.scopeKey,
        definitionId: instance.definitionId,
        definitionName: definition?.name ?? 'View',
        generatedAt: instance.generatedAt,
        scopeLabel,
        scopeLabelUnresolved: !allResolved,
      };
    })
    .sort(
      (a, b) =>
        new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime()
    );
}
